import axios from "axios";
import { useEffect, useState } from "react";
import {
  Calendar2Week,
  CheckCircle,
  Clock,
  XCircle,
} from "react-bootstrap-icons";

import PageHeader from "../../components/PageHeader";
import LoadingSpinner from "../../components/LoadingSpinner";
import { ApiConnectionString } from "../../constants/ApiConntection";
import { useAuthContext } from "../../hooks/useAuthContext";

import "./BookAppointmentPage.css";

export default function BookAppointmentPage() {
  const { user } = useAuthContext();

  const [availableDays, setAvailableDays] = useState([]);
  const [appointments, setAppointments] = useState([]);

  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedSlot, setSelectedSlot] = useState(null);

  const [selectedBookedDate, setSelectedBookedDate] = useState(null);
  const [selectedBookedAppointments, setSelectedBookedAppointments] =
    useState([]);

  const [calendarMonth, setCalendarMonth] = useState(new Date());
  const [step, setStep] = useState("date");

  const [isLoading, setIsLoading] = useState(true);
  const [isBooking, setIsBooking] = useState(false);

  const [err, setErr] = useState("");
  const [bookingErr, setBookingErr] = useState("");

  useEffect(() => {
    if (user?.token) {
      fetchPageData();
    }
  }, [user]);

  const fetchPageData = async ({
    resetFlow = true,
    showLoader = true,
  } = {}) => {
    try {
      if (showLoader) {
        setIsLoading(true);
      }

      setErr("");

      if (resetFlow) {
        setBookingErr("");
      }

      const [availabilityResponse, appointmentsResponse] = await Promise.all([
        axios.get(`${ApiConnectionString}/api/client/Availablity`, {
          headers: {
            Authorization: `Bearer ${user.token}`,
          },
        }),

        axios.get(`${ApiConnectionString}/api/client/appointments`, {
          headers: {
            Authorization: `Bearer ${user.token}`,
          },
        }),
      ]);

      console.log(availabilityResponse.data)

      const days =
        availabilityResponse.data.availableDays ||
        availabilityResponse.data.AvailableDays ||
        [];

      const bookedAppointments = Array.isArray(appointmentsResponse.data)
        ? appointmentsResponse.data
        : appointmentsResponse.data.appointments ||
          appointmentsResponse.data.Appointments ||
          [];

      setAvailableDays(days);
      setAppointments(bookedAppointments);

      const firstAvailableDate = days.length > 0 ? getDateOnly(days[0]) : "";

      const firstBookedDate =
        bookedAppointments.length > 0
          ? getAppointmentDateKey(bookedAppointments[0])
          : "";

      const firstCalendarDate = firstAvailableDate || firstBookedDate;

      if (firstCalendarDate) {
        setCalendarMonth(parseDateOnly(firstCalendarDate));
      }

      if (resetFlow) {
        setSelectedDate(null);
        setSelectedSlot(null);
        setSelectedBookedDate(null);
        setSelectedBookedAppointments([]);
        setStep("date");
      }

      return {
        days,
        bookedAppointments,
      };
    } catch (error) {
      console.error(error);

      if (error.response?.status === 403) {
        setErr(
          "You do not currently have permission to self-book appointments."
        );
      } else {
        setErr("Failed to load appointment information.");
      }

      return {
        days: [],
        bookedAppointments: [],
      };
    } finally {
      if (showLoader) {
        setIsLoading(false);
      }
    }
  };

  const selectDay = (day) => {
    setSelectedDate(day);
    setSelectedSlot(null);
    setBookingErr("");
    setStep("time");
  };

  const viewBookedDay = (date, bookedAppointments) => {
    setSelectedBookedDate(date);
    setSelectedBookedAppointments(bookedAppointments);
    setBookingErr("");
    setStep("booked");
  };

  const selectTime = (slot) => {
    setSelectedSlot(slot);
    setBookingErr("");
  };

  const reviewAppointment = () => {
    if (!selectedDate || !selectedSlot) {
      return;
    }

    setBookingErr("");
    setStep("review");
  };

  const confirmBooking = async () => {
    if (!selectedDate || !selectedSlot) {
      return;
    }

    try {
      setIsBooking(true);
      setBookingErr("");

      const start = getSlotStart(selectedSlot);

      await axios.post(
        `${ApiConnectionString}/api/client/appointment`,
        {
          start,
        },
        {
          headers: {
            Authorization: `Bearer ${user.token}`,
            "Content-Type": "application/json",
          },
        }
      );

      setStep("confirmed");
    } catch (error) {
      console.error(error);

      const responseMessage = error.response?.data;

      const message =
        typeof responseMessage === "string"
          ? responseMessage
          : "This appointment could not be booked. It may no longer be available.";

      const refreshedData = await fetchPageData({
        resetFlow: false,
        showLoader: false,
      });

      const selectedDateKey = getDateOnly(selectedDate);

      const refreshedSelectedDate = refreshedData.days.find(
        (day) => getDateOnly(day) === selectedDateKey
      );

      setSelectedSlot(null);

      if (refreshedSelectedDate) {
        setSelectedDate(refreshedSelectedDate);
        setStep("time");
      } else {
        setSelectedDate(null);
        setStep("date");
      }

      setBookingErr(message);
    } finally {
      setIsBooking(false);
    }
  };

  const changeDate = () => {
    setSelectedSlot(null);
    setBookingErr("");
    setStep("date");
  };

  const changeAppointment = () => {
    setBookingErr("");
    setStep("time");
  };

  const getDateOnly = (day) => {
    if (!day) return "";

    return day.date || day.Date || "";
  };

  const getDayDate = (day) => {
    if (!day) return "";

    return day.displayDate || day.DisplayDate || day.date || day.Date || "";
  };

  const getDaySlots = (day) => {
    if (!day) return [];

    return day.slots || day.Slots || [];
  };

  const getSlotTime = (slot) => {
    if (!slot) return "";

    return slot.time || slot.Time || "";
  };

  const getSlotStart = (slot) => {
    if (!slot) return "";

    return slot.start || slot.Start || "";
  };

  const getSlotFinish = (slot) => {
    if (!slot) return "";

    return slot.finish || slot.Finish || "";
  };

  const formatSlotTimeRange = (slot) => {
    if (!slot) return "";

    const directTime = getSlotTime(slot);

    if (directTime && directTime.includes("–")) {
      return directTime;
    }

    const startValue = getSlotStart(slot);
    const finishValue = getSlotFinish(slot);

    if (!startValue) {
      return directTime || "";
    }

    const start = new Date(startValue);
    const finish = finishValue ? new Date(finishValue) : null;

    if (Number.isNaN(start.getTime())) {
      return directTime || "";
    }

    const startTime = start.toLocaleTimeString("en-GB", {
      hour: "2-digit",
      minute: "2-digit",
    });

    if (!finish || Number.isNaN(finish.getTime())) {
      return startTime;
    }

    const finishTime = finish.toLocaleTimeString("en-GB", {
      hour: "2-digit",
      minute: "2-digit",
    });

    return `${startTime} – ${finishTime}`;
  };

  const getAppointmentStart = (appointment) => {
    if (!appointment) return "";

    return (
      appointment.start ||
      appointment.Start ||
      appointment.appointmentDate ||
      appointment.AppointmentDate ||
      appointment.dateAndTime ||
      appointment.DateAndTime ||
      appointment.date ||
      appointment.Date ||
      ""
    );
  };

  const getAppointmentFinish = (appointment) => {
    if (!appointment) return "";

    return (
      appointment.finish ||
      appointment.Finish ||
      appointment.finishTime ||
      appointment.FinishTime ||
      appointment.end ||
      appointment.End ||
      ""
    );
  };

  const getAppointmentTitle = (appointment) => {
    if (!appointment) return "Therapy appointment";

    return (
      appointment.name ||
      appointment.Name ||
      appointment.title ||
      appointment.Title ||
      "Therapy appointment"
    );
  };

  const getAppointmentDateKey = (appointment) => {
    const start = getAppointmentStart(appointment);

    if (!start) {
      return "";
    }

    if (typeof start === "string" && /^\d{4}-\d{2}-\d{2}/.test(start)) {
      return start.substring(0, 10);
    }

    const date = new Date(start);

    if (Number.isNaN(date.getTime())) {
      return "";
    }

    return formatDateKey(date);
  };

  const getAppointmentsForDate = (date) => {
    if (!date) return [];

    const dateKey =
      typeof date === "string" ? date.substring(0, 10) : formatDateKey(date);

    return appointments.filter(
      (appointment) => getAppointmentDateKey(appointment) === dateKey
    );
  };

  const formatAppointmentDate = (appointment) => {
    const directDisplayDate = appointment.displayDate || appointment.DisplayDate;

    if (directDisplayDate) {
      return directDisplayDate;
    }

    const start = getAppointmentStart(appointment);
    const date = new Date(start);

    if (!start || Number.isNaN(date.getTime())) {
      return "Date unavailable";
    }

    return date.toLocaleDateString("en-GB", {
      weekday: "long",
      day: "numeric",
      month: "long",
      year: "numeric",
    });
  };

  const formatAppointmentTime = (appointment) => {
    const directTime = appointment.time || appointment.Time;

    if (directTime) {
      return directTime;
    }

    const directStartTime = appointment.startTime || appointment.StartTime;

    const directFinishTime = appointment.finishTime || appointment.FinishTime;

    if (directStartTime && directFinishTime) {
      return `${directStartTime} – ${directFinishTime}`;
    }

    if (directStartTime) {
      return directStartTime;
    }

    const start = new Date(getAppointmentStart(appointment));
    const finishValue = getAppointmentFinish(appointment);
    const finish = finishValue ? new Date(finishValue) : null;

    if (Number.isNaN(start.getTime())) {
      return "Time unavailable";
    }

    const startTime = start.toLocaleTimeString("en-GB", {
      hour: "2-digit",
      minute: "2-digit",
    });

    if (!finish || Number.isNaN(finish.getTime())) {
      return startTime;
    }

    const finishTime = finish.toLocaleTimeString("en-GB", {
      hour: "2-digit",
      minute: "2-digit",
    });

    return `${startTime} – ${finishTime}`;
  };

  const parseDateOnly = (dateString) => {
    if (!dateString) return new Date();

    const parts = dateString.split("-");

    if (parts.length !== 3) {
      return new Date(dateString);
    }

    return new Date(
      Number(parts[0]),
      Number(parts[1]) - 1,
      Number(parts[2])
    );
  };

  const formatMonthTitle = (date) => {
    return date.toLocaleDateString("en-GB", {
      month: "long",
      year: "numeric",
    });
  };

  const formatDayNumber = (date) => {
    return date.getDate();
  };

  const formatDateKey = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");

    return `${year}-${month}-${day}`;
  };

  const formatAccessibleDate = (date) => {
    return date.toLocaleDateString("en-GB", {
      weekday: "long",
      day: "numeric",
      month: "long",
      year: "numeric",
    });
  };

  const goToPreviousMonth = () => {
    setCalendarMonth(
      new Date(calendarMonth.getFullYear(), calendarMonth.getMonth() - 1, 1)
    );
  };

  const goToNextMonth = () => {
    setCalendarMonth(
      new Date(calendarMonth.getFullYear(), calendarMonth.getMonth() + 1, 1)
    );
  };

  const getCalendarDays = () => {
    const year = calendarMonth.getFullYear();
    const month = calendarMonth.getMonth();

    const firstDayOfMonth = new Date(year, month, 1);
    const lastDayOfMonth = new Date(year, month + 1, 0);

    const days = [];

    const startPadding =
      firstDayOfMonth.getDay() === 0 ? 6 : firstDayOfMonth.getDay() - 1;

    for (let i = 0; i < startPadding; i++) {
      days.push(null);
    }

    for (let day = 1; day <= lastDayOfMonth.getDate(); day++) {
      days.push(new Date(year, month, day));
    }

    return days;
  };

  const findAvailableDay = (date) => {
    if (!date) return null;

    const dateKey = formatDateKey(date);

    return availableDays.find((day) => getDateOnly(day) === dateKey);
  };

  const getCurrentStepIndex = () => {
    if (step === "time") return 1;
    if (step === "review") return 2;

    return 0;
  };

  const renderProgress = () => {
    const steps = ["Date", "Time", "Confirm"];
    const currentStepIndex = getCurrentStepIndex();

    return (
      <div className="book-progress" aria-label="Appointment booking progress">
        {steps.map((label, index) => {
          const isActive = index === currentStepIndex;
          const isCompleted = index < currentStepIndex;

          return (
            <div
              key={label}
              className={`book-progress-step ${isActive ? "active" : ""} ${
                isCompleted ? "completed" : ""
              }`}
              aria-current={isActive ? "step" : undefined}
            >
              <span className="book-progress-number">
                {isCompleted ? <CheckCircle /> : index + 1}
              </span>

              <span className="book-progress-label">{label}</span>
            </div>
          );
        })}
      </div>
    );
  };

  if (isLoading) {
    return (
      <div className="book-page">
        <LoadingSpinner />
      </div>
    );
  }

  if (err) {
    return (
      <div className="book-page">
        <PageHeader
          title="Book appointment"
          text="Choose an available time for your next therapy session."
        />

        <div className="book-success-card">
          <div className="book-success-icon book-error-icon">
            <XCircle />
          </div>

          <h1>Appointments unavailable</h1>
          <p>{err}</p>

          <button
            type="button"
            className="book-primary-btn"
            onClick={() => fetchPageData()}
          >
            Try again
          </button>
        </div>
      </div>
    );
  }

  if (step === "booked") {
    return (
      <div className="book-page">
        <PageHeader
          title="Existing appointment"
          text="You already have an appointment booked on this date."
        />

        <div className="book-success-card book-existing-card">
          <div className="book-success-icon book-existing-icon">
            <Calendar2Week />
          </div>

          <h1>You’re already booked in</h1>

          <p>
            You already have an appointment on{" "}
            {selectedBookedDate
              ? formatAccessibleDate(selectedBookedDate)
              : "this date"}
            .
          </p>

          <div className="book-existing-list">
            {selectedBookedAppointments.map((appointment, index) => (
              <div
                className="book-existing-appointment"
                key={
                  appointment.id ||
                  appointment.Id ||
                  `${getAppointmentStart(appointment)}-${index}`
                }
              >
                <h2>{getAppointmentTitle(appointment)}</h2>

                <div className="book-existing-details">
                  <div>
                    <span>Date</span>
                    <strong>{formatAppointmentDate(appointment)}</strong>
                  </div>

                  <div>
                    <span>Time</span>
                    <strong>{formatAppointmentTime(appointment)}</strong>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <button
            type="button"
            className="book-secondary-btn"
            onClick={() => setStep("date")}
          >
            Back to calendar
          </button>
        </div>
      </div>
    );
  }

  if (step === "confirmed") {
    return (
      <div className="book-page">
        <div className="book-success-card">
          <div className="book-success-icon">
            <CheckCircle />
          </div>

          <h1>Appointment booked</h1>

          <p>
            Your appointment has been booked and your therapist has been
            notified by email.
          </p>

          <div className="book-summary-box">
            <div>
              <span>Date</span>
              <strong>{getDayDate(selectedDate)}</strong>
            </div>

            <div>
              <span>Time</span>
              <strong>{formatSlotTimeRange(selectedSlot)}</strong>
            </div>
          </div>

          <button
            type="button"
            className="book-primary-btn"
            onClick={() => fetchPageData()}
          >
            Back to calendar
          </button>
        </div>
      </div>
    );
  }

  if (availableDays.length === 0 && appointments.length === 0) {
    return (
      <div className="book-page">
        <PageHeader
          title="Book appointment"
          text="Choose an available time for your next therapy session."
        />

        <div className="book-success-card">
          <div className="book-success-icon">
            <Calendar2Week />
          </div>

          <h1>No appointments</h1>

          <p>
            There are currently no available appointment times or upcoming
            appointments.
          </p>
        </div>
      </div>
    );
  }

  if (step === "review") {
    return (
      <div className="book-page">
        <PageHeader
          title="Confirm appointment"
          text="Check the appointment details before booking."
        />

        {renderProgress()}

        <div className="book-success-card book-review-card">
          <div className="book-success-icon">
            <Calendar2Week />
          </div>

          <h1>Is this correct?</h1>

          <p>Your appointment will be added to your therapist’s diary.</p>

          {bookingErr && (
            <div className="book-error-box">
              <XCircle />
              <span>{bookingErr}</span>
            </div>
          )}

          <div className="book-summary-box">
            <div>
              <span>Date</span>
              <strong>{getDayDate(selectedDate)}</strong>
            </div>

            <div>
              <span>Time</span>
              <strong>{formatSlotTimeRange(selectedSlot)}</strong>
            </div>
          </div>

          <button
            type="button"
            className="book-primary-btn"
            disabled={isBooking}
            onClick={confirmBooking}
          >
            {isBooking ? "Booking appointment..." : "Confirm appointment"}
          </button>

          <button
            type="button"
            className="book-secondary-btn"
            disabled={isBooking}
            onClick={changeAppointment}
          >
            Change appointment
          </button>
        </div>
      </div>
    );
  }

  if (step === "time") {
    return (
      <div className="book-page">
        <PageHeader
          title="Choose a time"
          text={`Available appointments for ${getDayDate(selectedDate)}.`}
        />

        {renderProgress()}

        <section className="book-card book-step-card">
          <div className="book-section-header">
            <div className="book-icon">
              <Clock />
            </div>

            <div>
              <h2>{getDayDate(selectedDate)}</h2>
              <p>Select an available appointment time.</p>
            </div>
          </div>

          {bookingErr && (
            <div className="book-error-box">
              <XCircle />
              <span>{bookingErr}</span>
            </div>
          )}

          <div className="book-time-grid">
            {getDaySlots(selectedDate).map((slot) => {
              const slotStart = getSlotStart(slot);
              const slotFinish = getSlotFinish(slot);
              const slotTime = getSlotTime(slot);
              const selectedSlotStart = getSlotStart(selectedSlot);
              const isSelected = selectedSlotStart === slotStart;

              return (
                <button
                  type="button"
                  key={`${slotStart}-${slotFinish}`}
                  className={`book-time-btn ${isSelected ? "active" : ""}`}
                  aria-pressed={isSelected}
                  disabled={isBooking}
                  onClick={() => selectTime(slot)}
                >
                  {slotTime}
                </button>
              );
            })}
          </div>

          <button
            type="button"
            className="book-primary-btn"
            disabled={!selectedSlot || isBooking}
            onClick={reviewAppointment}
          >
            Continue
          </button>

          <button
            type="button"
            className="book-secondary-btn"
            disabled={isBooking}
            onClick={changeDate}
          >
            Choose a different date
          </button>
        </section>
      </div>
    );
  }

  return (
    <div className="book-page">
      <PageHeader
        title="Book appointment"
        text={
          availableDays.length > 0
            ? "Choose an available date or view an existing appointment."
            : "View your existing booked appointments."
        }
      />

      {renderProgress()}

      <section className="book-card book-step-card">
        <div className="book-section-header">
          <div className="book-icon">
            <Calendar2Week />
          </div>

          <div>
            <h2>Choose a date</h2>
            <p>
              Available dates are green. Your booked appointments are purple.
            </p>
          </div>
        </div>

        {bookingErr && (
          <div className="book-error-box">
            <XCircle />
            <span>{bookingErr}</span>
          </div>
        )}

        <div className="book-calendar-key">
          <div>
            <span className="book-calendar-key-colour available" />
            Available
          </div>

          <div>
            <span className="book-calendar-key-colour booked" />
            Already booked
          </div>
        </div>

        <div className="book-calendar-header">
          <button
            type="button"
            aria-label="Previous month"
            onClick={goToPreviousMonth}
          >
            ‹
          </button>

          <strong>{formatMonthTitle(calendarMonth)}</strong>

          <button
            type="button"
            aria-label="Next month"
            onClick={goToNextMonth}
          >
            ›
          </button>
        </div>

        <div className="book-calendar-weekdays" aria-hidden="true">
          <span>Mon</span>
          <span>Tue</span>
          <span>Wed</span>
          <span>Thu</span>
          <span>Fri</span>
          <span>Sat</span>
          <span>Sun</span>
        </div>

        <div className="book-calendar-grid">
          {getCalendarDays().map((date, index) => {
            if (!date) {
              return (
                <div
                  key={`blank-${index}`}
                  className="book-calendar-day book-calendar-day-blank"
                  aria-hidden="true"
                />
              );
            }

            const availableDay = findAvailableDay(date);
            const bookedAppointments = getAppointmentsForDate(date);

            const isAvailable = Boolean(availableDay);
            const isBooked = bookedAppointments.length > 0;

            const dateKey = formatDateKey(date);

            const accessibleLabel = isBooked
              ? `${formatAccessibleDate(date)}, already booked`
              : isAvailable
                ? `${formatAccessibleDate(date)}, available`
                : `${formatAccessibleDate(date)}, unavailable`;

            return (
              <button
                type="button"
                key={dateKey}
                className={`book-calendar-day ${isAvailable ? "available" : ""} ${
                  isBooked ? "booked" : ""
                }`}
                aria-label={accessibleLabel}
                disabled={!isAvailable && !isBooked}
                onClick={() => {
                  if (isBooked) {
                    viewBookedDay(date, bookedAppointments);
                    return;
                  }

                  selectDay(availableDay);
                }}
              >
                <span>{formatDayNumber(date)}</span>
              </button>
            );
          })}
        </div>
      </section>
    </div>
  );
}